import { create } from 'zustand';
import type { MeView, Role } from './types';

/**
 * Issue #401 — 管理端会话存储安全化：
 *   - accessToken / user 仅保存在内存（zustand，无 persist），刷新页面后由
 *     HttpOnly Cookie 静默续期恢复（见 api.ts bootstrapSession）；
 *   - refreshToken 全程不进入前端 JS：由后端写入 HttpOnly + Secure + SameSite
 *     Cookie，XSS / 恶意扩展 / 共享机器都无法从 localStorage 读取；
 *   - 历史版本（zustand persist）落在 localStorage 的会话在启动时清除，
 *     旧 refreshToken 尽力向后端撤销（见 takeLegacyPersistedSession 调用方）。
 */

interface AuthState {
  accessToken: string | null;
  user: MeView | null;
  /**
   * 启动期 Cookie 静默续期是否已完成。完成前路由守卫渲染「验证中」而不是
   * 误判未登录跳 /login（否则每次刷新页面都会闪一次登录页）。
   */
  sessionReady: boolean;
  setSession: (payload: { accessToken: string; user?: MeView | null }) => void;
  setUser: (user: MeView | null) => void;
  setSessionReady: () => void;
  clear: () => void;
}

export const ADMIN_ROLES: Role[] = ['SUPER_ADMIN', 'ADMIN'];

/** V1.0.6 及之前 zustand persist 使用的 localStorage key（Issue #401 起废弃）。 */
export const LEGACY_SESSION_STORAGE_KEY = 'jzcollege.admin.session.v1';

export const useAuthStore = create<AuthState>()((set) => ({
  accessToken: null,
  user: null,
  sessionReady: false,
  setSession: ({ accessToken, user }) =>
    set((prev) => ({
      accessToken,
      user: user ?? prev.user,
      // 显式登录成功即视为会话就绪，不必等启动期静默续期。
      sessionReady: true,
    })),
  setUser: (user) => set({ user }),
  setSessionReady: () => set({ sessionReady: true }),
  clear: () => set({ accessToken: null, user: null }),
}));

/** 旧版 persist 载荷中我们关心的令牌字段（其余一律丢弃）。 */
export interface LegacyPersistedSession {
  accessToken: string | null;
  refreshToken: string | null;
}

/**
 * Issue #401 迁移兼容：读取并删除旧版 persist 在 localStorage 的会话。
 * 返回其中的令牌对（供调用方尽力向后端撤销旧 refreshToken），解析失败也保证
 * key 被删除——无论如何 localStorage 不再保留 refreshToken，用户走重新登录路径。
 */
export function takeLegacyPersistedSession(
  storage: Pick<Storage, 'getItem' | 'removeItem'> | null = typeof localStorage ===
  'undefined'
    ? null
    : localStorage,
): LegacyPersistedSession | null {
  if (!storage) return null;
  let raw: string | null = null;
  try {
    raw = storage.getItem(LEGACY_SESSION_STORAGE_KEY);
  } finally {
    if (raw !== null) storage.removeItem(LEGACY_SESSION_STORAGE_KEY);
  }
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as {
      state?: { accessToken?: unknown; refreshToken?: unknown };
    };
    const accessToken =
      typeof parsed.state?.accessToken === 'string' ? parsed.state.accessToken : null;
    const refreshToken =
      typeof parsed.state?.refreshToken === 'string'
        ? parsed.state.refreshToken
        : null;
    return { accessToken, refreshToken };
  } catch {
    return { accessToken: null, refreshToken: null };
  }
}

export function isAdminRole(role: Role | undefined | null): boolean {
  if (!role) return false;
  return ADMIN_ROLES.includes(role);
}
